import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DjayAvatar, DjayBubble, djayVariants, getDjayMissionHeading, getDjayLabWelcome, getDjayModeIntro } from '.';
import type { DjaySize, DjayEmotion, DjayTier, DjayVariant, BubbleColorScheme } from '.';
const SIZES: {
  size: DjaySize;
  label: string;
  pixels: number;
}[] = [{
  size: 'xs',
  label: 'XS',
  pixels: 16
}, {
  size: 'sm',
  label: 'SM',
  pixels: 24
}, {
  size: 'md',
  label: 'MD',
  pixels: 32
}, {
  size: 'lg',
  label: 'LG',
  pixels: 48
}, {
  size: 'xl',
  label: 'XL',
  pixels: 64
}, {
  size: 'hero',
  label: 'Hero',
  pixels: 128
}];
const EMOTIONS: {
  emotion: DjayEmotion;
  label: string;
  icon: string;
}[] = [{
  emotion: 'happy',
  label: 'Happy',
  icon: '😊'
}, {
  emotion: 'excited',
  label: 'Excited',
  icon: '🤩'
}, {
  emotion: 'encouraging',
  label: 'Encouraging',
  icon: '🤗'
}, {
  emotion: 'celebrating',
  label: 'Celebrating',
  icon: '🎉'
}, {
  emotion: 'proud',
  label: 'Proud',
  icon: '🌟'
}, {
  emotion: 'thinking',
  label: 'Thinking',
  icon: '🤔'
}, {
  emotion: 'sleeping',
  label: 'Sleeping',
  icon: '😴'
}, {
  emotion: 'pointing',
  label: 'Pointing',
  icon: '👉'
}, {
  emotion: 'surprised',
  label: 'Surprised',
  icon: '😲'
}];
const TIERS: {
  tier: DjayTier;
  label: string;
  icon: string;
}[] = [{
  tier: 'rookie',
  label: 'Rookie',
  icon: '🌱'
}, {
  tier: 'pro',
  label: 'Pro',
  icon: '🌿'
}, {
  tier: 'master',
  label: 'Master',
  icon: '👑'
}];
const VARIANTS: {
  variant: DjayVariant;
  label: string;
}[] = [{
  variant: 'default',
  label: 'Default'
}, {
  variant: 'blaze',
  label: 'Blaze'
}, {
  variant: 'frost',
  label: 'Frost'
}, {
  variant: 'pixel',
  label: 'Pixel'
}, {
  variant: 'royal',
  label: 'Royal'
}, {
  variant: 'cosmic',
  label: 'Cosmic'
}, {
  variant: 'shadow',
  label: 'Shadow'
}, {
  variant: 'goud',
  label: 'Goud ✨'
}];
const BUBBLE_SCHEMES: {
  scheme: BubbleColorScheme;
  label: string;
}[] = [{
  scheme: 'default',
  label: 'Default'
}, {
  scheme: 'indigo',
  label: 'Indigo'
}, {
  scheme: 'amber',
  label: 'Amber'
}, {
  scheme: 'emerald',
  label: 'Emerald'
}, {
  scheme: 'violet',
  label: 'Violet'
}];
const MODE_IDS = ['fluency', 'sprint', 'challenge', 'experiments', 'assessment'];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 16
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 24
    }
  }
};
export function DjayShowcase() {
  const [activeSize, setActiveSize] = useState<DjaySize>('hero');
  const [activeEmotion, setActiveEmotion] = useState<DjayEmotion>('happy');
  const [activeTier, setActiveTier] = useState<DjayTier>('rookie');
  const [activeVariant, setActiveVariant] = useState<DjayVariant>('default');
  const [animationsEnabled, setAnimationsEnabled] = useState(true);
  const currentSizeConfig = SIZES.find(s => s.size === activeSize);
  const currentVariantConfig = VARIANTS.find(v => v.variant === activeVariant);
  const currentEmotionConfig = EMOTIONS.find(e => e.emotion === activeEmotion);
  const currentTierConfig = TIERS.find(t => t.tier === activeTier);
  return <div className="h-full overflow-y-auto bg-slate-50" data-id="element-3164">
      <motion.div className="max-w-6xl mx-auto p-6 pb-16 space-y-8" variants={containerVariants} initial="hidden" animate="visible" data-id="element-3165">
        {/* Section 1: Header */}
        <motion.div variants={itemVariants} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-gray-200 shadow-sm" data-id="element-3166">
          <div className="flex items-center gap-5" data-id="element-3167">
            <motion.div variants={djayVariants.celebrate} animate="animate" transition={{
            repeat: Infinity,
            repeatDelay: 3
          }} data-id="element-3168">
              <DjayAvatar size="xl" emotion="happy" animate={animationsEnabled} data-id="element-3169" />
            </motion.div>
            <div data-id="element-3170">
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-3171">
                Mascotte Djay
              </h1>
              <p className="text-gray-500 text-sm mt-1" data-id="element-3172">
                Beheer en preview van de Djay mascotte componenten.
              </p>
            </div>
          </div>
          <button onClick={() => setAnimationsEnabled(!animationsEnabled)} className={`text-sm font-medium px-4 py-2 rounded-full transition-colors whitespace-nowrap ${animationsEnabled ? 'bg-orange-100 text-orange-700 hover:bg-orange-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-3173">
            {animationsEnabled ? '✨ Animaties aan' : '⏸ Animaties uit'}
          </button>
        </motion.div>

        {/* Section 2: Unified Configurator */}
        <motion.div variants={itemVariants} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-3174">
          <div className="px-6 py-4 border-b border-gray-100" data-id="element-3175">
            <h2 className="font-semibold text-gray-900" data-id="element-3176">Configurator</h2>
          </div>

          <div className="p-6 flex flex-col lg:flex-row gap-8" data-id="element-3177">
            {/* Controls */}
            <div className="flex-1 space-y-6" data-id="element-3178">
              {/* Emotion Picker */}
              <div data-id="element-3179">
                <h3 className="text-sm font-medium text-gray-700 mb-3" data-id="element-3180">
                  Emotie
                </h3>
                <div className="flex flex-wrap gap-2" data-id="element-3181">
                  {EMOTIONS.map(({
                  emotion,
                  label,
                  icon
                }) => <button key={emotion} onClick={() => setActiveEmotion(emotion)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${activeEmotion === emotion ? 'bg-orange-500 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-3182">
                      <span data-id="element-3183">{icon}</span>
                      <span data-id="element-3184">{label}</span>
                    </button>)}
                </div>
              </div>

              {/* Tier Picker */}
              <div data-id="element-3185">
                <h3 className="text-sm font-medium text-gray-700 mb-3" data-id="element-3186">Tier</h3>
                <div className="inline-flex bg-gray-100 p-1 rounded-xl" data-id="element-3187">
                  {TIERS.map(({
                  tier,
                  label,
                  icon
                }) => <button key={tier} onClick={() => setActiveTier(tier)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTier === tier ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-3188">
                      <span data-id="element-3189">{icon}</span>
                      <span data-id="element-3190">{label}</span>
                    </button>)}
                </div>
              </div>

              {/* Variant Picker */}
              <div data-id="element-3191">
                <h3 className="text-sm font-medium text-gray-700 mb-3" data-id="element-3192">
                  Variant (Skin)
                </h3>
                <div className="flex flex-wrap gap-2" data-id="element-3193">
                  {VARIANTS.map(({
                  variant,
                  label
                }) => <button key={variant} onClick={() => setActiveVariant(variant)} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${activeVariant === variant ? 'bg-indigo-600 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-3194">
                      {label}
                    </button>)}
                </div>
              </div>

              {/* Size Picker */}
              <div data-id="element-3195">
                <h3 className="text-sm font-medium text-gray-700 mb-3" data-id="element-3196">
                  Formaat
                </h3>
                <div className="flex flex-wrap gap-2" data-id="element-3197">
                  {SIZES.map(({
                  size,
                  label,
                  pixels
                }) => <button key={size} onClick={() => setActiveSize(size)} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${activeSize === size ? 'bg-slate-800 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-3198">
                      {label}{' '}
                      <span className="opacity-70 font-normal" data-id="element-3199">
                        ({pixels}px)
                      </span>
                    </button>)}
                </div>
              </div>
            </div>

            {/* Active Preview */}
            <div className="flex flex-col items-center justify-center bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl border-2 border-dashed border-orange-200 p-8 min-w-[300px] lg:w-1/3" data-id="element-3200">
              <div className="flex-1 flex items-center justify-center min-h-[160px]" data-id="element-3201">
                <AnimatePresence mode="wait" data-id="element-3202">
                  <motion.div key={`${activeSize}-${activeEmotion}-${activeTier}-${activeVariant}`} initial={{
                  scale: 0.8,
                  opacity: 0
                }} animate={{
                  scale: 1,
                  opacity: 1
                }} exit={{
                  scale: 0.8,
                  opacity: 0
                }} transition={{
                  type: 'spring',
                  stiffness: 400,
                  damping: 25
                }} className="flex items-center justify-center" data-id="element-3203">
                    <DjayAvatar size={activeSize} emotion={activeEmotion} tier={activeTier} variant={activeVariant} animate={animationsEnabled} data-id="element-3204" />
                  </motion.div>
                </AnimatePresence>
              </div>
              <div className="mt-6 text-center bg-white/80 backdrop-blur-sm px-4 py-2 rounded-xl border border-orange-100 shadow-sm" data-id="element-3205">
                <div className="text-sm font-bold text-gray-800" data-id="element-3206">
                  {currentTierConfig?.label} · {currentVariantConfig?.label}
                </div>
                <div className="text-xs text-gray-500 mt-0.5" data-id="element-3207">
                  {currentEmotionConfig?.label} · {currentSizeConfig?.label} (
                  {currentSizeConfig?.pixels}px)
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Section 3: Matrix Overview */}
        <motion.div variants={itemVariants} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-3208">
          <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center" data-id="element-3209">
            <div data-id="element-3210">
              <h2 className="font-semibold text-gray-900" data-id="element-3211">
                Tier × Variant Matrix
              </h2>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-3212">
                Klik op een cel om deze in de configurator te laden. Toont
                huidige emotie: {currentEmotionConfig?.icon}{' '}
                {currentEmotionConfig?.label}.
              </p>
            </div>
          </div>
          <div className="p-6 overflow-x-auto" data-id="element-3213">
            <div className="min-w-[800px]" data-id="element-3214">
              {/* Header Row */}
              <div className="grid grid-cols-[120px_repeat(8,1fr)] gap-2 mb-2" data-id="element-3215">
                <div data-id="element-3216"></div>
                {VARIANTS.map(v => <div key={v.variant} className="text-center text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-3217">
                    {v.label}
                  </div>)}
              </div>

              {/* Tier Rows */}
              <div className="space-y-2" data-id="element-3218">
                {TIERS.map(t => <div key={t.tier} className="grid grid-cols-[120px_repeat(8,1fr)] gap-2 items-center" data-id="element-3219">
                    <div className="text-sm font-semibold text-gray-700 flex items-center gap-2 bg-gray-50 p-2 rounded-lg border border-gray-100" data-id="element-3220">
                      <span data-id="element-3221">{t.icon}</span>
                      <span data-id="element-3222">{t.label}</span>
                    </div>
                    {VARIANTS.map(v => {
                  const isSelected = activeTier === t.tier && activeVariant === v.variant;
                  return <button key={`${t.tier}-${v.variant}`} onClick={() => {
                    setActiveTier(t.tier);
                    setActiveVariant(v.variant);
                  }} className={`flex justify-center items-center p-3 rounded-xl border transition-all ${isSelected ? 'bg-orange-50 border-orange-400 ring-2 ring-orange-400 ring-offset-1' : 'bg-white border-gray-100 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-3223">
                          <DjayAvatar size="md" emotion={activeEmotion} tier={t.tier} variant={v.variant} animate={animationsEnabled} data-id="element-3224" />
                        </button>;
                })}
                  </div>)}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Section 4: Animations & Bubbles */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-3225">
          {/* Animations */}
          <motion.div variants={itemVariants} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-3226">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-3227">
              <h2 className="font-semibold text-gray-900" data-id="element-3228">
                Animaties (Framer Motion)
              </h2>
            </div>
            <div className="p-6 grid grid-cols-3 gap-4" data-id="element-3229">
              <div className="text-center" data-id="element-3230">
                <div className="bg-green-50 rounded-xl p-4 flex items-center justify-center h-32 mb-2" data-id="element-3231">
                  <motion.div variants={djayVariants.bounce} animate="animate" transition={{
                  repeat: Infinity,
                  repeatDelay: 2
                }} data-id="element-3232">
                    <DjayAvatar size="lg" emotion="excited" animate={false} data-id="element-3233" />
                  </motion.div>
                </div>
                <div className="text-xs font-medium text-gray-700" data-id="element-3234">Bounce</div>
              </div>
              <div className="text-center" data-id="element-3235">
                <div className="bg-amber-50 rounded-xl p-4 flex items-center justify-center h-32 mb-2" data-id="element-3236">
                  <motion.div variants={djayVariants.celebrate} animate="animate" transition={{
                  repeat: Infinity,
                  repeatDelay: 2
                }} data-id="element-3237">
                    <DjayAvatar size="lg" emotion="celebrating" animate={false} data-id="element-3238" />
                  </motion.div>
                </div>
                <div className="text-xs font-medium text-gray-700" data-id="element-3239">
                  Celebrate
                </div>
              </div>
              <div className="text-center" data-id="element-3240">
                <div className="bg-indigo-50 rounded-xl p-4 flex items-center justify-center h-32 mb-2" data-id="element-3241">
                  <motion.div variants={djayVariants.tierEvolution} animate="animate" transition={{
                  repeat: Infinity,
                  repeatDelay: 2
                }} data-id="element-3242">
                    <DjayAvatar size="lg" emotion="surprised" animate={false} data-id="element-3243" />
                  </motion.div>
                </div>
                <div className="text-xs font-medium text-gray-700" data-id="element-3244">
                  Evolution
                </div>
              </div>
            </div>
          </motion.div>

          {/* Bubbles */}
          <motion.div variants={itemVariants} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-3245">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-3246">
              <h2 className="font-semibold text-gray-900" data-id="element-3247">Speech Bubbles</h2>
            </div>
            <div className="p-6 flex flex-col gap-3" data-id="element-3248">
              {BUBBLE_SCHEMES.map(({
              scheme,
              label
            }) => <div key={scheme} className="flex items-center gap-4" data-id="element-3249">
                  <div className="w-20 text-xs font-bold text-gray-500 uppercase tracking-wider text-right" data-id="element-3250">
                    {label}
                  </div>
                  <div className="flex-1" data-id="element-3251">
                    <DjayBubble message={`Dit is de ${label.toLowerCase()} bubble variant.`} colorScheme={scheme} autoDismissMs={0} position="bottom" className="w-full" data-id="element-3252" />
                  </div>
                </div>)}
            </div>
          </motion.div>
        </div>

        {/* Section 5: Messages Preview */}
        <motion.div variants={itemVariants} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-3253">
          <div className="px-6 py-4 border-b border-gray-100" data-id="element-3254">
            <h2 className="font-semibold text-gray-900" data-id="element-3255">
              Berichten Registry Preview
            </h2>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-3256">
            <div data-id="element-3257">
              <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-3258">
                Mission Heading
              </div>
              <div className="flex flex-col gap-2" data-id="element-3259">
                {Array.from({
                length: 3
              }).map((_, i) => <div key={i} className="bg-slate-50 text-slate-800 text-sm px-3 py-2 rounded-lg border border-slate-200" data-id="element-3260">
                    {getDjayMissionHeading()}
                  </div>)}
              </div>
            </div>

            <div data-id="element-3261">
              <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-3262">
                Lab Welcome
              </div>
              <div className="flex flex-col gap-2" data-id="element-3263">
                {Array.from({
                length: 3
              }).map((_, i) => {
                const msg = getDjayLabWelcome();
                return <div key={i} className="flex items-center gap-2 bg-indigo-50 text-indigo-800 text-sm px-3 py-2 rounded-lg border border-indigo-100" data-id="element-3264">
                      <DjayAvatar size="xs" emotion={msg.emotion} animate={false} data-id="element-3265" />
                      <span data-id="element-3266">{msg.text}</span>
                    </div>;
              })}
              </div>
            </div>

            <div data-id="element-3267">
              <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-3268">
                Mode Intros
              </div>
              <div className="flex flex-col gap-2" data-id="element-3269">
                {MODE_IDS.slice(0, 3).map(modeId => {
                const msg = getDjayModeIntro(modeId);
                return <div key={modeId} className="flex items-start gap-2 bg-amber-50 text-amber-900 text-sm px-3 py-2 rounded-lg border border-amber-100" data-id="element-3270">
                      <DjayAvatar size="xs" emotion={msg.emotion} animate={false} className="mt-0.5 shrink-0" data-id="element-3271" />
                      <div data-id="element-3272">
                        <span className="font-bold text-[10px] uppercase block text-amber-700 mb-0.5" data-id="element-3273">
                          {modeId}
                        </span>
                        {msg.text}
                      </div>
                    </div>;
              })}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>;
}